import React from "react";

import {
  FiAlertTriangle,
  FiBox,
  FiCalendar,
  FiCheckCircle,
  FiDollarSign,
  FiEdit2,
  FiFileText,
  FiHash,
  FiImage,
  FiPackage,
  FiRefreshCcw,
  FiTag,
  FiTruck,
  FiX,
} from "react-icons/fi";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const getConditionLabel = (condition) => {
  const labels = {
    new: "New",
    used: "Used",
    refurbished: "Refurbished",
    "open-box": "Open Box",
  };

  return labels[condition] || "-";
};

const getStatusLabel = (status) => {
  const labels = {
    active: "Active",
    inactive: "Inactive",
    draft: "Draft",
  };

  return labels[status] || "-";
};

const getStatusClass = (status) => {
  switch (status) {
    case "active":
      return "inventory-product-drawer-status-active";

    case "draft":
      return "inventory-product-drawer-status-draft";

    default:
      return "inventory-product-drawer-status-inactive";
  }
};

const getCategoryName = (category) => {
  if (
    category &&
    typeof category === "object"
  ) {
    return category.name || "-";
  }

  return "-";
};

const getImageList = (product) => {
  const images = Array.isArray(
    product?.images
  )
    ? product.images
    : [];

  const thumbnail =
    product?.thumbnail || "";

  return [
    ...new Set(
      [thumbnail, ...images].filter(Boolean)
    ),
  ];
};

const ProductDrawer = ({
  open,
  product,
  onClose,
  onEdit,
  onRefresh,
}) => {
  if (!open || !product) {
    return null;
  }

  const stockQty = Number(
    product?.stockQty || 0
  );

  const reservedQty = Number(
    product?.reservedQty || 0
  );

  const availableQty = Math.max(
    stockQty - reservedQty,
    0
  );

  const lowStockThreshold = Number(
    product?.lowStockThreshold || 0
  );

  const isOutOfStock =
    stockQty <= 0;

  const isLowStock =
    stockQty <= lowStockThreshold;

  const sellingPrice = Number(
    product?.sellingPrice || 0
  );

  const discountedPrice = Number(
    product?.discountedPrice || 0
  );

  const purchasePrice = Number(
    product?.purchasePrice || 0
  );

  const effectiveSellingPrice =
    discountedPrice > 0 &&
    discountedPrice < sellingPrice
      ? discountedPrice
      : sellingPrice;

  const estimatedProfitPerUnit =
    effectiveSellingPrice -
    purchasePrice;

  const images =
    getImageList(product);

  const attributes = Array.isArray(
    product?.attributes
  )
    ? product.attributes
    : [];

  const tags = Array.isArray(
    product?.tags
  )
    ? product.tags
    : [];

  return (
    <div className="inventory-product-drawer-overlay">
      <button
        type="button"
        className="inventory-product-drawer-backdrop"
        onClick={onClose}
        aria-label="Close product details"
      />

      <aside
        className="inventory-product-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Product details"
      >
        <div className="inventory-product-drawer-header">
          <div>
            <span>
              INVENTORY PRODUCT
            </span>

            <h2>
              Product Details
            </h2>
          </div>

          <button
            type="button"
            className="inventory-product-drawer-close"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <FiX />
          </button>
        </div>

        <div className="inventory-product-drawer-body">
          <section className="inventory-product-drawer-card inventory-product-drawer-overview">
            <div className="inventory-product-drawer-main-image">
              {product?.thumbnail ? (
                <img
                  src={`${process.env.REACT_APP_API_URL}/${product?.thumbnail}`}
                  alt={
                    product?.name ||
                    "Product"
                  }
                  onError={(event) => {
                    event.currentTarget.style.display =
                      "none";
                  }}
                />
              ) : (
                <FiImage />
              )}
            </div>

            <div className="inventory-product-drawer-main-info">
              <div className="inventory-product-drawer-badges">
                <span
                  className={`inventory-product-drawer-status ${getStatusClass(
                    product?.status
                  )}`}
                >
                  {getStatusLabel(
                    product?.status
                  )}
                </span>

                <span className="inventory-product-drawer-condition">
                  {getConditionLabel(
                    product?.condition
                  )}
                </span>

                {product?.isFeatured ? (
                  <span className="inventory-product-drawer-featured">
                    Featured
                  </span>
                ) : null}
              </div>

              <h3>
                {product?.name || "-"}
              </h3>

              <p>
                {product?.shortDescription ||
                  "No short description available."}
              </p>

              <div className="inventory-product-drawer-price-row">
                <strong>
                  {formatCurrency(
                    effectiveSellingPrice
                  )}
                </strong>

                {discountedPrice > 0 &&
                discountedPrice <
                  sellingPrice ? (
                  <span>
                    {formatCurrency(
                      sellingPrice
                    )}
                  </span>
                ) : null}
              </div>
            </div>
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiBox />
              Stock Overview
            </h3>

            <div className="inventory-product-drawer-stock-grid">
              <div>
                <span>
                  Current Stock
                </span>

                <strong>
                  {stockQty}
                </strong>
              </div>

              <div>
                <span>
                  Reserved
                </span>

                <strong>
                  {reservedQty}
                </strong>
              </div>

              <div>
                <span>
                  Available
                </span>

                <strong>
                  {availableQty}
                </strong>
              </div>

              <div>
                <span>
                  Low Stock Limit
                </span>

                <strong>
                  {lowStockThreshold}
                </strong>
              </div>
            </div>

            <div
              className={`inventory-product-drawer-stock-alert ${
                isOutOfStock
                  ? "out"
                  : isLowStock
                    ? "low"
                    : "ok"
              }`}
            >
              {isOutOfStock ? (
                <>
                  <FiAlertTriangle />

                  <span>
                    Product is out of stock.
                  </span>
                </>
              ) : isLowStock ? (
                <>
                  <FiAlertTriangle />

                  <span>
                    Product stock is below the configured threshold.
                  </span>
                </>
              ) : (
                <>
                  <FiCheckCircle />

                  <span>
                    Product stock level is healthy.
                  </span>
                </>
              )}
            </div>
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiPackage />
              Product Information
            </h3>

            <div className="inventory-product-drawer-detail-grid">
              <div>
                <span>
                  SKU
                </span>

                <strong>
                  {product?.sku || "-"}
                </strong>
              </div>

              <div>
                <span>
                  Barcode
                </span>

                <strong>
                  {product?.barcode ||
                    "-"}
                </strong>
              </div>

              <div>
                <span>
                  Brand
                </span>

                <strong>
                  {product?.brand || "-"}
                </strong>
              </div>

              <div>
                <span>
                  Model Number
                </span>

                <strong>
                  {product?.modelNumber ||
                    "-"}
                </strong>
              </div>

              <div>
                <span>
                  Category
                </span>

                <strong>
                  {getCategoryName(
                    product?.category
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Sub Category
                </span>

                <strong>
                  {getCategoryName(
                    product?.subCategory
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Product Type
                </span>

                <strong>
                  {product?.productType ||
                    "-"}
                </strong>
              </div>

              <div>
                <span>
                  Unit
                </span>

                <strong>
                  {product?.unit || "-"}
                </strong>
              </div>

              <div>
                <span>
                  Color
                </span>

                <strong>
                  {product?.color || "-"}
                </strong>
              </div>

              <div>
                <span>
                  Size
                </span>

                <strong>
                  {product?.size || "-"}
                </strong>
              </div>

              <div>
                <span>
                  Warranty
                </span>

                <strong>
                  {product?.warranty || "-"}
                </strong>
              </div>

              <div>
                <span>
                  Tax
                </span>

                <strong>
                  {Number(
                    product?.taxPercentage ||
                      0
                  )}
                  %
                </strong>
              </div>
            </div>
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiDollarSign />
              Pricing Information
            </h3>

            <div className="inventory-product-drawer-price-grid">
              <div>
                <span>
                  Purchase Price
                </span>

                <strong>
                  {formatCurrency(
                    purchasePrice
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Selling Price
                </span>

                <strong>
                  {formatCurrency(
                    sellingPrice
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Discounted Price
                </span>

                <strong>
                  {discountedPrice > 0
                    ? formatCurrency(
                        discountedPrice
                      )
                    : "-"}
                </strong>
              </div>

              <div>
                <span>
                  Profit Per Unit
                </span>

                <strong
                  className={
                    estimatedProfitPerUnit >=
                    0
                      ? "profit"
                      : "loss"
                  }
                >
                  {formatCurrency(
                    estimatedProfitPerUnit
                  )}
                </strong>
              </div>
            </div>
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiTag />
              Dynamic Attributes
            </h3>

            {attributes.length > 0 ? (
              <div className="inventory-product-drawer-attribute-list">
                {attributes.map(
                  (attribute, index) => (
                    <div
                      key={`${attribute?.name}-${index}`}
                    >
                      <span>
                        {attribute?.name ||
                          "-"}
                      </span>

                      <strong>
                        {attribute?.value ||
                          "-"}
                      </strong>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="inventory-product-drawer-empty">
                No dynamic attributes added.
              </div>
            )}
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiFileText />
              Description
            </h3>

            <div className="inventory-product-drawer-description">
              {product?.description ||
                "No description available."}
            </div>
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiTruck />
              Supplier Information
            </h3>

            <div className="inventory-product-drawer-detail-grid">
              <div>
                <span>
                  Supplier Name
                </span>

                <strong>
                  {product?.supplier?.name ||
                    "-"}
                </strong>
              </div>

              <div>
                <span>
                  Supplier Mobile
                </span>

                <strong>
                  {product?.supplier
                    ?.mobile || "-"}
                </strong>
              </div>

              <div>
                <span>
                  Invoice Number
                </span>

                <strong>
                  {product?.supplier
                    ?.invoiceNumber || "-"}
                </strong>
              </div>
            </div>
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiImage />
              Product Images
            </h3>

            {images.length > 0 ? (
              <div className="inventory-product-drawer-gallery">
                {images.map(
                  (image, index) => (
                    <a
                      key={`${image}-${index}`}
                      href={`${process.env.REACT_APP_API_URL}/${image}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={`${process.env.REACT_APP_API_URL}/${image}`}
                        alt={`Product ${
                          index + 1
                        }`}
                        onError={(event) => {
                          event.currentTarget.style.display =
                            "none";
                        }}
                      />
                    </a>
                  )
                )}
              </div>
            ) : (
              <div className="inventory-product-drawer-empty">
                No product images available.
              </div>
            )}
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiHash />
              Tags
            </h3>

            {tags.length > 0 ? (
              <div className="inventory-product-drawer-tags">
                {tags.map(
                  (tag, index) => (
                    <span
                      key={`${tag}-${index}`}
                    >
                      {tag}
                    </span>
                  )
                )}
              </div>
            ) : (
              <div className="inventory-product-drawer-empty">
                No product tags added.
              </div>
            )}
          </section>

          <section className="inventory-product-drawer-card">
            <h3>
              <FiCalendar />
              Timeline
            </h3>

            <div className="inventory-product-drawer-detail-grid">
              <div>
                <span>
                  Created On
                </span>

                <strong>
                  {formatDate(
                    product?.createdAt
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Last Updated
                </span>

                <strong>
                  {formatDate(
                    product?.updatedAt
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Deal End
                </span>

                <strong>
                  {formatDate(
                    product?.dealEnd
                  )}
                </strong>
              </div>
            </div>
          </section>
        </div>

        <div className="inventory-product-drawer-footer">
          <button
            type="button"
            className="inventory-product-drawer-refresh"
            onClick={onRefresh}
          >
            <FiRefreshCcw />
            Refresh
          </button>

          <button
            type="button"
            className="inventory-product-drawer-edit"
            onClick={() =>
              onEdit(product)
            }
          >
            <FiEdit2 />
            Edit Product
          </button>
        </div>
      </aside>
    </div>
  );
};

export default ProductDrawer;